import { useState } from 'react';
import type { PredictResponse } from '../types';
import { formatYen } from '../lib/format';
import { track } from '../lib/analytics';

interface Props {
  result: PredictResponse;
  shareUrl: string;
  propertyType: 'condo' | 'house';
  floorPlan: string;
  buildingYear: number;
}

const HASHTAGS = ['マプカチ', '不動産相場'];

function buildText(p: Props): string {
  const { result, propertyType, floorPlan, buildingYear } = p;
  const age = Math.max(0, new Date().getFullYear() - buildingYear);
  const ptLabel = propertyType === 'condo' ? '中古マンション' : '戸建て';
  const loc = result.location.districtName
    ? `${result.location.municipalityName} / ${result.location.districtName}`
    : result.location.municipalityName;
  const station = result.location.nearbyStations[0];
  return [
    'マプカチ｜全国 不動産価格予測',
    `${loc} ${floorPlan} ${ptLabel}（築${age}年）`,
    `推定 ${formatYen(result.predictedPrice)}（幅 ${formatYen(result.priceRange.low)}〜${formatYen(result.priceRange.high)}）`,
    station ? `${station.name}駅 約${station.distanceM.toLocaleString()}m` : '',
  ].filter(Boolean).join('\n');
}

const open = (url: string) => window.open(url, '_blank', 'noopener,noreferrer');

export default function ShareButtons(props: Props) {
  const [copied, setCopied] = useState(false);
  const text = buildText(props);
  const { shareUrl } = props;

  const onX = () => {
    track('share_click', { network: 'x' });
    open(
      `https://x.com/intent/post?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}&hashtags=${HASHTAGS.join(',')}`,
    );
  };

  const onLine = () => {
    track('share_click', { network: 'line' });
    open(
      `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`,
    );
  };

  const onFb = () => {
    track('share_click', { network: 'facebook' });
    open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`);
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${text}\n${shareUrl}`);
      setCopied(true);
      track('share_click', { network: 'copy' });
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // fall through silently; older browsers without clipboard API
    }
  };

  const canWebShare = typeof navigator !== 'undefined' && 'share' in navigator;
  const onWebShare = async () => {
    track('share_click', { network: 'webshare' });
    try {
      await (navigator as Navigator & { share: (d: ShareData) => Promise<void> }).share({
        title: 'マプカチ｜全国 不動産価格予測',
        text,
        url: shareUrl,
      });
    } catch {
      // user cancelled or share unsupported -- ignore
    }
  };

  const Btn = (props: {
    label: string;
    title: string;
    onClick: () => void;
    children: React.ReactNode;
  }) => (
    <button
      type="button"
      onClick={props.onClick}
      title={props.title}
      aria-label={props.title}
      className="flex h-9 w-9 items-center justify-center rounded border border-rule bg-sheet text-ink-2 shadow-sm transition-colors hover:border-primary hover:bg-primary-wash hover:text-primary-dark"
    >
      {props.children}
    </button>
  );

  return (
    <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-rule pt-4">
      <span className="mr-1 text-[11px] tracking-[0.14em] text-ink-3">共有</span>
      <Btn label="X" title="X (Twitter) でシェア" onClick={onX}>
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden>
          <path d="M18.244 2H21l-6.52 7.45L22 22h-6.797l-5.32-6.96L3.8 22H1.04l6.97-7.96L1 2h6.93l4.81 6.36L18.244 2zm-1.193 18h1.873L7.04 4H5.06l11.99 16z"/>
        </svg>
      </Btn>
      <Btn label="LINE" title="LINEでシェア" onClick={onLine}>
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden>
          <path d="M19.365 9.89c.50 0 .906.41.906.91 0 .51-.41.92-.906.92h-1.27v.81h1.27c.50 0 .906.41.906.92 0 .50-.41.91-.906.91h-2.18a.91.91 0 0 1-.91-.91v-4.36c0-.50.41-.91.91-.91h2.18c.50 0 .906.41.906.91s-.41.91-.906.91h-1.27v.89h1.27zM15.27 13.36a.91.91 0 0 1-.74.40.91.91 0 0 1-.73-.36l-2.23-3.04v2.49c0 .50-.41.91-.91.91-.50 0-.91-.41-.91-.91v-4.36c0-.40.26-.74.63-.86a.92.92 0 0 1 1.04.34l2.23 3.04V8.55c0-.50.40-.91.91-.91.50 0 .91.41.91.91v4.36c0 .15-.04.30-.10.45zM8.16 13.36c-.04.10-.10.18-.18.26a.92.92 0 0 1-1.30 0l-1.27-1.27v.56c0 .50-.41.91-.91.91-.50 0-.91-.41-.91-.91v-4.36c0-.50.41-.91.91-.91.50 0 .91.41.91.91v.56l1.27-1.27a.92.92 0 0 1 1.30 1.30L6.29 11l1.61 1.61c.18.18.26.40.26.62 0 .04 0 .08-.01.13z"/>
          <path d="M12 2C6.48 2 2 5.78 2 10.45c0 4.18 3.55 7.69 8.36 8.35.32.07.76.21.87.49.10.25.07.64.03.89l-.14.85c-.04.25-.20.99.87.54 1.07-.45 5.79-3.41 7.91-5.84 1.46-1.61 2.16-3.24 2.16-5.27C22 5.78 17.52 2 12 2z"/>
        </svg>
      </Btn>
      <Btn label="Facebook" title="Facebookでシェア" onClick={onFb}>
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden>
          <path d="M22 12.07C22 6.5 17.52 2 12 2S2 6.5 2 12.07c0 5.02 3.66 9.18 8.44 9.93v-7.02H7.9v-2.91h2.54V9.85c0-2.51 1.49-3.89 3.77-3.89 1.09 0 2.24.19 2.24.19v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.44 2.91h-2.34V22c4.78-.75 8.44-4.91 8.44-9.93z"/>
        </svg>
      </Btn>
      <Btn label="コピー" title="リンクとテキストをコピー" onClick={onCopy}>
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
        </svg>
      </Btn>
      {canWebShare && (
        <Btn label="共有" title="OS共有メニュー" onClick={onWebShare}>
          <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
          </svg>
        </Btn>
      )}
      {copied && <span className="text-[12px] text-ink-2">コピーしました</span>}
    </div>
  );
}
